import React from 'react';
import { RefreshCw, MessageCircle, Trash2, CheckSquare, Square, Minus } from 'lucide-react';
import ThemedCard from '../../themed/ThemedCard';
import { RSVPResponseWithTable } from '../types';

interface MessagesTabProps {
  filteredMessages: RSVPResponseWithTable[];
  searchTerm: string;
  batchEditMode: boolean;
  selectedItems: Set<string>;
  toggleSelectAll: (items: RSVPResponseWithTable[]) => void;
  getSelectAllState: (items: RSVPResponseWithTable[]) => 'none' | 'all' | 'partial';
  toggleSelectItem: (id: string) => void;
  handleDeleteRSVP: (rsvpId: string, guestName: string) => void;
  deletingRSVPId: string | null;
}

export const MessagesTab: React.FC<MessagesTabProps> = ({
  filteredMessages,
  searchTerm,
  batchEditMode,
  selectedItems,
  toggleSelectAll,
  getSelectAllState,
  toggleSelectItem,
  handleDeleteRSVP,
  deletingRSVPId,
}) => {
  return (
    <ThemedCard className="p-6">
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold text-wedding-text-title mb-2">賓客留言</h3>
          <p className="text-wedding-text-body">收到 {filteredMessages.length} 則賓客留言</p>
        </div>
        {batchEditMode && filteredMessages.length > 0 && (
          <button
            onClick={() => toggleSelectAll(filteredMessages)}
            className="flex items-center space-x-2 px-3 py-2 text-sm text-wedding-text-body hover:text-wedding-pink border border-wedding-pink/30 rounded-md hover:bg-wedding-pink/5"
          >
            {(() => {
              const selectState = getSelectAllState(filteredMessages);
              if (selectState === 'all') return <CheckSquare className="h-4 w-4 text-wedding-pink" />;
              if (selectState === 'partial') return <Minus className="h-4 w-4 text-wedding-pink" />;
              return <Square className="h-4 w-4" />;
            })()}
            <span>
              {(() => {
                const selectState = getSelectAllState(filteredMessages);
                if (selectState === 'all') return '取消全選';
                if (selectState === 'partial') return '全選';
                return '全選';
              })()}
            </span>
          </button>
        )}
      </div>

      <div className="space-y-4">
        {filteredMessages.map((response) => (
          <div key={response.id} className={`border border-wedding-pink/10 rounded-lg p-4 ${
            batchEditMode ? 'relative' : ''
          }`}>
            {batchEditMode && (
              <div className="absolute top-4 left-4">
                <button
                  onClick={() => toggleSelectItem(response.id)}
                  className="flex items-center justify-center w-5 h-5 text-gray-400 hover:text-wedding-pink"
                >
                  {selectedItems.has(response.id) ? (
                    <CheckSquare className="h-4 w-4 text-wedding-pink" />
                  ) : (
                    <Square className="h-4 w-4" />
                  )}
                </button>
              </div>
            )}

            <div className={`flex justify-between items-start mb-3 ${
              batchEditMode ? 'ml-8' : ''
            }`}>
              <div>
                <h4 className="font-medium text-wedding-text-title">{response.guestName}</h4>
                <p className="text-sm text-wedding-text-body">{response.guestEmail}</p>
              </div>
              <div className="flex items-center gap-2">
                <span className={`px-2 py-1 text-xs rounded-full ${
                  response.rsvpData.attending === 'yes'
                    ? 'bg-green-100 text-green-700'
                    : 'bg-red-100 text-red-700'
                }`}>
                  {response.rsvpData.attending === 'yes' ? '確認出席' : '無法出席'}
                </span>
                <span className="text-xs text-gray-500">
                  {response.submittedAt.toLocaleDateString('zh-TW')}
                </span>
                {!batchEditMode && (
                  <button
                    onClick={() => handleDeleteRSVP(response.id, response.guestName)}
                    disabled={deletingRSVPId === response.id}
                    className="text-red-400 hover:text-red-600 disabled:opacity-50 ml-2"
                    title="刪除 RSVP 回覆"
                  >
                    {deletingRSVPId === response.id ? (
                      <RefreshCw className="h-4 w-4 animate-spin" />
                    ) : (
                      <Trash2 className="h-4 w-4" />
                    )}
                  </button>
                )}
              </div>
            </div>

            <div className={`bg-gray-50 rounded-md p-3 ${
              batchEditMode ? 'ml-8' : ''
            }`}>
              <p className="text-gray-800 italic">
                "{response.rsvpData.message || response.rsvpData.specialRequests}"
              </p>
            </div>
          </div>
        ))}
      </div>

      {filteredMessages.length === 0 && (
        <div className="text-center py-12">
          <MessageCircle className="h-12 w-12 text-wedding-text-body mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">尚無留言</h3>
          <p className="text-gray-600">
            {searchTerm ? '調整搜尋條件以查看更多結果' : '賓客回覆時留下的訊息會顯示在這裡'}
          </p>
        </div>
      )}
    </ThemedCard>
  );
};
